import { Badge, DataTable, type DataTableColumn } from "@bruntworkwear-apps/ui";
import { money, quantity, rate } from "@/shared/lib";
import type { QuantityAllocation } from "@/shared/api";
import styles from "../PoBreakdown.module.css";

const COLUMNS: DataTableColumn<QuantityAllocation>[] = [
  { key: "sheet", header: "Sheet", render: (entry) => entry.sheet, secondary: true },
  { key: "row", header: "Row", render: (entry) => entry.row_number, numeric: true },
  { key: "product", header: "Product", render: (entry) => entry.product || "—" },
  { key: "ship", header: "Shipped", render: (entry) => entry.ship_date ?? "no date" },
  {
    key: "shipped_qty",
    header: "Qty shipped",
    render: (entry) => quantity(entry.shipped_quantity),
    numeric: true,
  },
  {
    key: "received",
    header: "Received",
    render: (entry) => quantity(entry.units_received),
    numeric: true,
  },
  {
    key: "taken",
    header: "Units used",
    // The number that explains the match: what this row actually contributed.
    render: (entry) =>
      entry.units_taken > 0 ? (
        quantity(entry.units_taken)
      ) : (
        <span className={styles.skipped}>{entry.skipped_reason ?? "not used"}</span>
      ),
    numeric: true,
  },
  {
    key: "rate",
    header: "Customs rate",
    render: (entry) => rate(entry.customs_rate),
    numeric: true,
  },
  {
    key: "invoice",
    header: "CBP invoice",
    render: (entry) => money(entry.invoice_amount),
    numeric: true,
  },
  {
    key: "state",
    header: "State",
    render: (entry) =>
      entry.units_taken <= 0 ? (
        <Badge tone="muted">unused</Badge>
      ) : entry.is_partial ? (
        <Badge tone="warning">partial</Badge>
      ) : (
        <Badge tone="success">full</Badge>
      ),
  },
];

/**
 * Every tracker row considered for this order, drawn from or not.
 *
 * The unused rows are the point. For an order that could not be explained -
 * PO 1306 is 2,988 units short - this list is the answer to "why", and hiding it
 * would leave the reviewer to open the workbook and work it out by hand.
 *
 * A private sub-view of PoBreakdown.
 */
export function TrackerRowsView({ entries }: { entries: QuantityAllocation[] }) {
  return (
    <DataTable
      rows={entries}
      columns={COLUMNS}
      rowKey={(entry) => `${entry.sheet}-${entry.row_number}`}
      rowTone={(entry) =>
        entry.units_taken <= 0 ? "muted" : entry.is_partial ? "warning" : "default"
      }
      maxHeight="18rem"
      emptyMessage="No tracker rows matched this order within the date window."
    />
  );
}
